import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { exhaustMap, of } from 'rxjs';
import { State } from '../index';
import { DepartmentDataService } from '../../services/department-data.service';
import { isNotNullOrUndefined } from '../../../helper/helper';
import { catchError, concatMap, map, withLatestFrom } from 'rxjs/operators';
import { BaseActionTypes } from '../actions/base.actions';
import { DepartmentActions } from '../actions/department.actions';
import { getDepartmentDictionary } from '../selectors/department.selector';

enum ErrorCodes {
    Load = 'Abteilungen Abrufen fehlgeschlagen',
    Modify = 'Abteilungen Bearbeiten fehlgeschlagen',
    Remove = 'Abteilungen Löschen fehlgeschlagen',
    Add = 'Abteilungen Hinzufügen fehlgeschlagen',
}

@Injectable()
export class DepartmentEffects {
    GetDepartment = createEffect(
        () =>
            this.actions$.pipe(
                ofType(DepartmentActions.loadRequest),
                exhaustMap(({ Payload }) => {
                    return this.api.get$({ UpdatedSince: Payload.updatedSince }).pipe(
                        withLatestFrom(this.store$.select(getDepartmentDictionary)),
                        map(([res, state]) => {
                            if (!isNotNullOrUndefined(Payload.updatedSince)) {
                                return DepartmentActions.loadSuccess({ Payload: res });
                            } else {
                                const fRes = res.filter((p) => {
                                    const fromState = state[p.Id];
                                    return !fromState || fromState.UpdatedAt.getTime() !== p.UpdatedAt.getTime();
                                });
                                return DepartmentActions.loadSuccess({ Payload: fRes });
                            }
                        }),
                        catchError((err) => of(DepartmentActions.loadFailure({ Errors: err, ToasterMessage: ErrorCodes.Load }))),
                    );
                }),
            ),
    );

    EditDepartment = createEffect(() =>
        this.actions$.pipe(
            ofType(DepartmentActions.changeRequest),
            concatMap(({ Payload }) =>
                this.api.change$(Payload).pipe(
                    map((res) => DepartmentActions.changeSuccess({ Payload: res })),
                    catchError((err) => of(DepartmentActions.changeFailure({ Errors: err, ToasterMessage: ErrorCodes.Modify }))),
                ),
            ),
        ),
    );

    AddDepartment = createEffect(() =>
        this.actions$.pipe(
            ofType(DepartmentActions.addRequest),
            concatMap(({ Payload }) =>
                this.api.add$(Payload).pipe(
                    map((res) => DepartmentActions.addSuccess({ Payload: res })),
                    catchError((err) => of(DepartmentActions.addFailure({ Errors: err, ToasterMessage: ErrorCodes.Add }))),
                ),
            ),
        ),
    );

    DeleteDepartment = createEffect(() =>
        this.actions$.pipe(
            ofType(DepartmentActions.deleteRequest),
            concatMap(({ Payload }) =>
                this.api.delete$(Payload).pipe(
                    map((res) => DepartmentActions.deleteSuccess({ Payload: +res.Id })),
                    catchError((err) => of(DepartmentActions.deleteFailure({ Errors: err, ToasterMessage: ErrorCodes.Remove }))),
                ),
            ),
        ),
    );

    constructor(private actions$: Actions, private store$: Store<State>, private api: DepartmentDataService) {}

    Error = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    DepartmentActions.loadFailure,
                    DepartmentActions.addFailure,
                    DepartmentActions.changeFailure,
                    DepartmentActions.deleteFailure,
                ),
                map(({ Errors, ToasterMessage }) =>
                    BaseActionTypes.ErrorAction({
                        Payload: {
                            ToasterMessage,
                            Err: Errors,
                        },
                    }),
                ),
            ),
    );
}
